import { Box, Button, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core'
import axios from 'axios'
import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import VehicleForm from './VehicleForm'

const useStyles = makeStyles((theme) => ({
  dialog: {
    '&.modal-dialog': {
      width: 'calc(100% - 32px)',
      maxWidth: 1140,
      margin: '1.75rem auto'
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main
  },
  message: {
    marginBottom: theme.spacing(2)
  }
}))

const VehicleManagementModal = ({ vehicles, loading, error, onReload, onClose, onVehicleSaved, onVehicleDeleted }) => {
  const classes = useStyles()
  const [view, setView] = useState('list')
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [busy, setBusy] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const showList = () => {
    setView('list')
    setSelectedVehicle(null)
    setDeleteError('')
  }

  const vehicleSaved = (vehicle) => {
    onVehicleSaved(vehicle)
    showList()
  }

  const deleteVehicle = async () => {
    if (busy) return
    setBusy(true)
    setDeleteError('')
    let deleted = false
    try {
      const { data } = await axios.delete(`vehicles/${selectedVehicle._id}`)
      if (data.success) {
        deleted = true
      } else {
        setDeleteError(data.message || 'Automjeti nuk u fshi. Provoni përsëri.')
      }
    } catch (error) {
      setDeleteError(
        error.response?.status === 403
          ? 'Nuk keni të drejtë të fshini automjete.'
          : error.response?.data?.message || 'Automjeti nuk u fshi. Provoni përsëri.'
      )
    } finally {
      setBusy(false)
    }
    if (deleted) {
      onVehicleDeleted(selectedVehicle)
      showList()
    }
  }

  const title =
    view === 'add'
      ? 'Shto automjet'
      : view === 'edit'
        ? `Modifiko automjetin ${selectedVehicle.license_plate}`
        : view === 'delete'
          ? 'Delete vehicle'
          : 'Menaxho automjetet'

  return (
    <Modal
      show
      dialogClassName={classes.dialog}
      className='modal-1'
      onHide={() => {
        if (!busy) onClose()
      }}
      backdrop={busy ? 'static' : true}
      keyboard={!busy}
      aria-labelledby='vehicle-management-title'
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title id='vehicle-management-title'>{title}</Modal.Title>
      </Modal.Header>
      {(view === 'add' || view === 'edit') && (
        <VehicleForm vehicle={selectedVehicle} onClose={showList} onSaved={vehicleSaved} onBusyChange={setBusy} />
      )}
      {view === 'list' && (
        <>
          <Modal.Body>
            <Box className={classes.toolbar}>
              <Typography variant='body1'>Automjetet e regjistruara</Typography>
              <Button variant='contained' color='primary' onClick={() => setView('add')}>
                Shto automjet
              </Button>
            </Box>
            {error && (
              <Box role='alert' className={classes.message}>
                <Typography color='error'>{error}</Typography>
                <Button onClick={onReload}>Provo përsëri</Button>
              </Box>
            )}
            <TableContainer>
              <Table size='small' aria-label='Automjetet e regjistruara'>
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle ID</TableCell>
                    <TableCell>Numri i targës</TableCell>
                    <TableCell>Marka</TableCell>
                    <TableCell>Modeli</TableCell>
                    <TableCell>Vendet për pasagjerë</TableCell>
                    <TableCell>Veprimet</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading || error || vehicles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align='center'>
                        {loading
                          ? 'Duke ngarkuar automjetet…'
                          : error
                            ? 'Lista nuk është e disponueshme.'
                            : 'Nuk ka automjete të regjistruara.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    vehicles.map((vehicle) => (
                      <TableRow key={vehicle._id}>
                        <TableCell>{vehicle.vehicle_id || '—'}</TableCell>
                        <TableCell component='th' scope='row'>
                          {vehicle.license_plate}
                        </TableCell>
                        <TableCell>{vehicle.make}</TableCell>
                        <TableCell>{vehicle.model}</TableCell>
                        <TableCell>{vehicle.passenger_seats}</TableCell>
                        <TableCell>
                          <Box className={classes.actions}>
                            <Button
                              size='small'
                              variant='outlined'
                              aria-label={`Modifiko automjetin ${vehicle.license_plate}`}
                              onClick={() => {
                                setSelectedVehicle(vehicle)
                                setView('edit')
                              }}
                            >
                              Modifiko
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              className={classes.deleteButton}
                              aria-label={`Delete vehicle ${vehicle.license_plate}`}
                              onClick={() => {
                                setSelectedVehicle(vehicle)
                                setDeleteError('')
                                setView('delete')
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={onClose}>Mbyll</Button>
          </Modal.Footer>
        </>
      )}
      {view === 'delete' && (
        <>
          <Modal.Body>
            <Typography>
              Dëshironi ta fshini automjetin me targën <strong>{selectedVehicle.license_plate}</strong>?
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              Automjeti nuk do të jetë më i disponueshëm për rezervime.
            </Typography>
            {deleteError && (
              <Typography role='alert' color='error'>
                {deleteError}
              </Typography>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={showList} disabled={busy}>
              Anulo
            </Button>
            <Button onClick={deleteVehicle} variant='outlined' className={classes.deleteButton} disabled={busy}>
              {busy ? 'Duke fshirë…' : 'Delete'}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  )
}

export default VehicleManagementModal
